/**
 * Course Management Page for trAInedup.ai
 * Allows admins and instructors to manage their courses
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  Copy,
  MoreVertical,
  BookOpen,
  Clock,
  Users,
  Star,
  Trash2,
  Globe,
  EyeOff,
} from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { useAuth } from '../contexts/AuthContext';
import {
  Course,
} from '../types/course';
import { UserRole } from '../types';
import { ROUTES } from '../utils/constants';

/**
 * Course management page component
 */
export const CourseManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState<number>(50);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [pageStack, setPageStack] = useState<any[]>([]); // stack of cursors to support Prev
  const [stats, setStats] = useState<{ total: number; published: number; draft: number; totalEnrollments: number }>({ total: 0, published: 0, draft: 0, totalEnrollments: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showStatusPanel, setShowStatusPanel] = useState(false);

  // Check if user has permission to manage courses
  const canManageCourses = hasRole(UserRole.ADMIN) || hasRole(UserRole.INSTRUCTOR);

  /**
   * Load courses from Firestore (real data)
   */
  const loadCourses = useCallback(async (opts?: { startAfterDoc?: any; reset?: boolean }) => {
    try {
      setLoading(true);
      // Build filters from UI state
      const filters = {
        searchTerm: searchTerm || undefined,
        // Optionally map typeFilter to category or type if needed
        status: (statusFilter === 'published' || statusFilter === 'draft')
          ? (statusFilter as 'published' | 'draft')
          : undefined,
      };
      const { courses: fetchedCourses, hasMore, lastDoc } = await import('../services/firebase/courses')
        .then(m => m.courseService.getCourses(filters, { pageSize, startAfterDoc: opts?.startAfterDoc }));
      let filteredCourses = fetchedCourses;
      if (hasRole(UserRole.INSTRUCTOR) && !hasRole(UserRole.ADMIN)) {
        filteredCourses = fetchedCourses.filter(course => course.createdBy === user?.uid);
      }
      setCourses(filteredCourses);
      setHasMore(hasMore);
      setLastDoc(lastDoc);
      if (opts?.reset) {
        setPageStack([]);
      } else if (opts?.startAfterDoc) {
        setPageStack(prev => [...prev, opts.startAfterDoc]);
      }
    } catch (error) {
      // Error loading courses
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [hasRole, user?.uid, searchTerm, statusFilter, pageSize]);

  const loadStats = useCallback(async () => {
    try {
      const { courseService } = await import('../services/firebase/courses');
      const s = await courseService.getCourseStats(
        hasRole(UserRole.INSTRUCTOR) && !hasRole(UserRole.ADMIN) ? { createdBy: user?.uid } : undefined
      );
      setStats(s);
    } catch (e) {
      setStats({ total: 0, published: 0, draft: 0, totalEnrollments: 0 });
    }
  }, [hasRole, user?.uid]);

  useEffect(() => {
    if (!canManageCourses) {
      navigate(ROUTES.dashboard);
      return;
    }
    loadCourses({ reset: true });
    loadStats();
  }, [canManageCourses, navigate, loadCourses, loadStats]);

  /**
   * Filter courses based on search and filters
   */
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = true; // Category filter removed
    const matchesStatus = !statusFilter || 
                         (statusFilter === 'published' && course.isPublished) ||
                         (statusFilter === 'draft' && !course.isPublished);
    const matchesTag = tagFilters.length === 0 || (course.tags && tagFilters.some(tag => course.tags.includes(tag)));

    return matchesSearch && matchesCategory && matchesStatus && matchesTag;
  });

  /**
   * Handle individual course actions
   */
  const handleCourseAction = async (courseId: string, action: 'publish' | 'unpublish' | 'delete') => {
    try {
      const { courseService } = await import('../services/firebase/courses');
      switch (action) {
        case 'publish':
          await courseService.updateCourse(courseId, { isPublished: true });
          break;
        case 'unpublish':
          await courseService.updateCourse(courseId, { isPublished: false });
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
            await courseService.deleteCourse(courseId);
          } else {
            return; // User cancelled, don't reload
          }
          break;
      }
      loadCourses();
    } catch (error) {
      console.error(`Error performing ${action} action:`, error);
    }
  };

  /**
   * Get status badge
   */
  const getStatusBadge = (course: Course) => {
    if (course.isPublished) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Published
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Draft
        </span>
      );
    }
  };

  /**
   * Course card component
   */
  const CourseCard: React.FC<{ course: Course }> = ({ course }) => {
    // Use local fallback image if thumbnail is missing or invalid
    const fallbackImage = '/tulogo.png';
    const isValidThumbnail = course.thumbnail && typeof course.thumbnail === 'string' && course.thumbnail.startsWith('http');
    const thumbnailSrc = isValidThumbnail ? course.thumbnail : fallbackImage;
    return (
      <Card className="overflow-hidden transition-all duration-200">
        <div className="relative">
          <img
            src={thumbnailSrc}
            alt={course.title}
            className="w-full h-48 object-cover"
            onError={e => { (e.currentTarget as HTMLImageElement).src = fallbackImage; }}
          />
          <div className="absolute top-4 left-4">
            {getStatusBadge(course)}
          </div>
          <div className="absolute bottom-4 left-4">
            <div className="flex items-center space-x-1 px-2 py-1 bg-black bg-opacity-50 rounded text-white text-xs">
              <BookOpen className="w-3 h-3" />
              <span>Course</span>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-lg text-neutral-900 line-clamp-2">
              {course.title}
            </h3>
            <div className="ml-2">
              <button className="text-neutral-400 hover:text-neutral-600">
                <MoreVertical className="w-5 h-5" />
              </button>
            </div>
          </div>

          <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
            {course.shortDescription}
          </p>

          <div className="flex items-center justify-between text-sm text-neutral-500 mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>{course.enrollmentCount}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4" />
                <span>{course.averageRating.toFixed(1)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{Math.round(course.estimatedDuration / 60)}h</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={Eye}
              onClick={() => navigate(`/courses/${course.id}`)}
            >
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={Edit}
              onClick={() => navigate(`/dashboard/admin/courses/${course.id}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={Copy}
              onClick={() => {/* TODO: Implement duplicate */}}
            >
              Duplicate
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={course.isPublished ? EyeOff : Globe}
              onClick={() => handleCourseAction(course.id, course.isPublished ? 'unpublish' : 'publish')}
            >
              {course.isPublished ? 'Unpublish' : 'Publish'}
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={Trash2}
              onClick={() => handleCourseAction(course.id, 'delete')}
            >
              Delete
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  if (!canManageCourses) {
    return null;
  }

  return (
    <Layout>
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="font-montserrat font-bold text-3xl text-neutral-800 mb-2">
                Course Management
              </h1>
              <p className="text-lg text-neutral-600">
                Create and manage your courses
              </p>
            </div>
            
            <div className="mt-6 lg:mt-0">
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => navigate(ROUTES.adminCoursesCreate)}
              >
                Create Course
              </Button>
            </div>
          </div>

      {/* Stats Cards (global) */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
                <div>
          <p className="text-2xl font-bold text-neutral-900">{stats.total}</p>
                  <p className="text-sm text-neutral-600">Total Courses</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <Eye className="w-6 h-6 text-green-600" />
                </div>
                <div>
          <p className="text-2xl font-bold text-neutral-900">{stats.published}</p>
                  <p className="text-sm text-neutral-600">Published</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {
        if (stats.draft > 0) {
                setStatusFilter('draft');
              }
            }}>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
                  <Edit className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
          <p className="text-2xl font-bold text-neutral-900">{stats.draft}</p>
                  <p className="text-sm text-neutral-600">Drafts</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
          <p className="text-2xl font-bold text-neutral-900">{stats.totalEnrollments}</p>
                  <p className="text-sm text-neutral-600">Total Enrollments</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Filters and Search */}
          <Card className="p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon={Search}
                />
              </div>
              <div className="flex items-center space-x-4">
                {/* Tag Filter Floating Panel */}
                <div className="relative">
                  <Button
                    variant={showFilters ? "primary" : "outline"}
                    icon={Filter}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    Filters
                  </Button>
                  {showFilters && (
                    <div className="fixed z-50 top-auto left-auto mt-2 bg-white border border-neutral-300 rounded-lg shadow-lg p-6 w-80 flex flex-col items-start"
                      style={{ maxHeight: '300px', overflowY: 'auto', right: '24px', top: '120px', maxWidth: 'calc(100vw - 48px)' }}>
                      <div className="flex items-center justify-between w-full mb-4">
                        <span className="font-semibold text-neutral-800">Filter by Tag</span>
                        <button
                          className="text-neutral-400 hover:text-neutral-600 text-lg"
                          onClick={() => setShowFilters(false)}
                          aria-label="Close"
                        >
                          ×
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 w-full">
                        <Button
                          variant={tagFilters.length === 0 ? "primary" : "outline"}
                          size="sm"
                          onClick={() => setTagFilters([])}
                        >
                          All Tags
                        </Button>
                        {Array.from(new Set(courses.flatMap(course => course.tags || []))).map(tag => (
                          <Button
                            key={tag}
                            variant={tagFilters.includes(tag) ? "primary" : "outline"}
                            size="sm"
                            onClick={() => {
                              setTagFilters(prev => prev.includes(tag)
                                ? prev.filter(t => t !== tag)
                                : [...prev, tag]);
                            }}
                          >
                            {tag}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* Status Filter Floating Panel */}
                <div className="relative">
                  <Button
                    variant={showStatusPanel ? "primary" : "outline"}
                    icon={Clock}
                    onClick={() => setShowStatusPanel(!showStatusPanel)}
                  >
                    Status
                  </Button>
                  {showStatusPanel && (
                    <div className="fixed z-50 top-auto left-auto mt-2 bg-white border border-neutral-300 rounded-lg shadow-lg p-6 w-64 flex flex-col items-start"
                      style={{ right: '24px', top: '120px', maxWidth: 'calc(100vw - 48px)' }}>
                      <div className="flex items-center justify-between w-full mb-4">
                        <span className="font-semibold text-neutral-800">Filter by Status</span>
                        <button
                          className="text-neutral-400 hover:text-neutral-600 text-lg"
                          onClick={() => setShowStatusPanel(false)}
                          aria-label="Close"
                        >
                          ×
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 w-full">
                        <Button
                          variant={statusFilter === '' ? "primary" : "outline"}
                          size="sm"
                          onClick={() => setStatusFilter('')}
                        >
                          All Status
                        </Button>
                        <Button
                          variant={statusFilter === 'published' ? "primary" : "outline"}
                          size="sm"
                          onClick={() => setStatusFilter('published')}
                        >
                          Published
                        </Button>
                        <Button
                          variant={statusFilter === 'draft' ? "primary" : "outline"}
                          size="sm"
                          onClick={() => setStatusFilter('draft')}
                        >
                          Draft
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Courses Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredCourses.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses.map(course => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
              {/* Pagination Controls */}
              <div className="flex items-center justify-between mt-8">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-600">Per page:</span>
                  <select
                    className="border border-neutral-300 rounded px-2 py-1 text-sm"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      // Reset to first page when page size changes
                      loadCourses({ reset: true });
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    disabled={pageStack.length === 0 || loading}
                    onClick={() => {
                      // Go to previous page: pop last cursor and reload starting after previous-previous
                      setPageStack(prev => {
                        const newStack = [...prev];
                        newStack.pop();
                        const prior = newStack[newStack.length - 1] || null;
                        loadCourses({ startAfterDoc: prior });
                        return newStack;
                      });
                    }}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!hasMore || loading}
                    onClick={() => {
                      if (lastDoc) {
                        loadCourses({ startAfterDoc: lastDoc });
                      }
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
              <h3 className="font-medium text-neutral-900 mb-2">No courses found</h3>
              <p className="text-neutral-600 mb-4">
                {searchTerm || statusFilter
                  ? 'Try adjusting your search criteria or filters.'
                  : 'Get started by creating your first course.'}
              </p>
              {!searchTerm && !statusFilter && (
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => navigate(ROUTES.adminCoursesCreate)}
                >
                  Create Your First Course
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

